import { useEffect, useState, type ReactNode } from 'react';
import { localeUI } from '../lib/format';
import { Camera, Plus, Trash2, Upload } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { useAuth, hasRole } from '../lib/auth';

type PhotoLink = {
  id: string; url: string; note: string | null; added_by: string | null; created_at: string;
  /** Borrado suave: tachado con quién y por qué; solo super-manager revierte. */
  deleted_at?: string | null; deleted_by?: string | null; deleted_reason?: string | null;
};

/**
 * Fotos del claim (CompanyCam / Drive), estilo Adjuster Info: si hay fotos muestra el
 * botón "Abrir fotos" (último link ACTIVO); si no, "Subir fotos". Historial con fecha y autor.
 *
 * BORRAR un link equivocado (pedido de Intake): la papelera pide el MOTIVO y el link queda
 * TACHADO — "deleted by {quien} — {motivo}" — sin perderse. Solo un super-manager lo revierte.
 * `onChanged` avisa al padre si el claim avanzó de etapa al agregar fotos.
 */
export function ClaimPhotosBox({ claimId, onChanged, compact, extra }: {
  claimId: string; onChanged?: (advanced: boolean) => void; compact?: boolean;
  /** Botones que van EN LA MISMA FILA, a la derecha de "Open photos" (la tarjeta del claim pone ahí los de videos). */
  extra?: ReactNode;
}) {
  const { t } = useI18n();
  const { me } = useAuth();
  const canRestore = hasRole(me, 'super-manager');
  const [links, setLinks] = useState<PhotoLink[]>([]);
  const [latest, setLatest] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<{ links: PhotoLink[]; latest: string | null }>(`/api/claims/${claimId}/photo-links`)
    .then((r) => { setLinks(r.links); setLatest(r.latest); }).then(limpiar).catch(marcar);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [claimId]);

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' });

  async function add() {
    if (!url.trim()) return;
    setBusy(true); setErr('');
    try {
      const r = await api.post<{ advanced: boolean }>(`/api/claims/${claimId}/photo-links`, { url: url.trim() });
      setUrl(''); setAdding(false); await load(); onChanged?.(r.advanced);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  /** Tacha un link con motivo (queda visible, con línea en el medio y quién lo borró). */
  async function removeLink(linkId: string) {
    const reason = window.prompt(t('photos.deleteReason'));
    if (reason === null) return;
    if (!reason.trim()) { setErr(t('photos.deleteReasonRequired')); return; }
    setBusy(true); setErr('');
    try { await api.post(`/api/claims/${claimId}/photo-links/${linkId}/delete`, { reason: reason.trim() }); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  async function restoreLink(linkId: string) {
    setBusy(true); setErr('');
    try { await api.post(`/api/claims/${claimId}/photo-links/${linkId}/restore`, {}); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  /** Un link tachado: línea en el medio + "deleted by X — motivo" + revert (super-manager). */
  const scratched = (l: PhotoLink, big = false) => (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <span style={{ textDecoration: 'line-through', color: 'var(--muted)', fontSize: big ? 'var(--fs-sm)' : 'var(--fs-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }} title={l.url}>
        <Camera size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {l.url}
      </span>
      <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)' }}>
        <Trash2 size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('photos.deletedBy', { user: l.deleted_by ?? '—', reason: l.deleted_reason ?? '' })}
        {canRestore && (
          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', marginLeft: 6, padding: '0 6px' }} disabled={busy} onClick={() => restoreLink(l.id)}>
            ↩ {t('photos.restore')}
          </button>
        )}
      </span>
    </span>
  );

  const hasPhotos = !!latest;
  const activeLink = links.find((l) => !l.deleted_at) ?? null;
  // El link más reciente está tachado y NO hay otro activo → se muestra tachado en el frente
  // (pedido del dueño: que se VEA que se borró, no que desaparezca).
  const newestDeleted = !hasPhotos && links.length > 0 && links[0].deleted_at ? links[0] : null;

  return (
    <div style={{ marginTop: compact ? 8 : 12, minWidth: 0 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Camera size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('photos.title')}</span>
        {links.length > 0 && (
          <button className="btn ghost sm" style={{ padding: '0 8px', fontSize: 'var(--fs-2xs)' }} onClick={() => setShowHist((v) => !v)}>
            {showHist ? '▾' : '▸'} {t('photos.history')} ({links.length})
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
        {hasPhotos ? (
          <>
            <a className="btn sm" href={latest!} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}><Camera size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('photos.open')}</a>
            {/* Papelera: tacha el link ACTIVO (con motivo). Cualquiera de office+ puede. */}
            {activeLink && (
              <button className="btn ghost sm" title={t('photos.deleteHint')} disabled={busy} onClick={() => removeLink(activeLink.id)}><Trash2 size={14} strokeWidth={1.75} /></button>
            )}
            {!adding && <button className="btn ghost sm" onClick={() => setAdding(true)}><Plus size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('photos.addLink')}</button>}
          </>
        ) : (
          <>
            {newestDeleted && scratched(newestDeleted, true)}
            {!adding && <button className="btn sm" onClick={() => setAdding(true)}><Upload size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('photos.upload')}</button>}
          </>
        )}
        {extra}
      </div>

      {adding && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input className="input sm" style={{ flex: 1, minWidth: 200 }} autoFocus value={url}
            onChange={(e) => setUrl(e.target.value)} placeholder="CompanyCam / Drive link"
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          <button className="btn sm" disabled={busy || !url.trim()} onClick={add}>{t('common.save')}</button>
          <button className="btn ghost sm" onClick={() => { setAdding(false); setUrl(''); setErr(''); }}>{t('common.cancel')}</button>
        </div>
      )}
      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)', marginTop: 4 }}>{err}</div>}

      {/* Historial de links (el más reciente primero). Los tachados se ven tachados. */}
      {showHist && links.length > 0 && (
        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
          {links.map((l) => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--fs-xs)', background: 'var(--surface-2)', borderRadius: 8, padding: '6px 8px' }}>
              {activeLink?.id === l.id && links.length > 1 && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)' }}>{t('photos.current')}</span>}
              {l.deleted_at ? scratched(l) : (
                <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{l.url}</a>
              )}
              <span style={{ flex: 1 }} />
              {!l.deleted_at && (
                <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', padding: '0 6px' }} title={t('photos.deleteHint')} disabled={busy} onClick={() => removeLink(l.id)}><Trash2 size={12} strokeWidth={1.75} /></button>
              )}
              {l.added_by && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{l.added_by}</span>}
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmt(l.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
